import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { Modal } from '@swtorbit/ui/react';
import {
  SocketEvent,
  GameType,
  type Registration,
  type FprRegistrationResponses,
  type RegistrationsExportFile,
  type RegistrationExportRecord,
  type ExportRegistrationsRequest,
  type ExportRegistrationsDataResponse,
  type RegistrationsImportedResponse,
  type RegistrationAddRequest,
  type RegistrationRemoveRequest,
  type RegistrationChangeAck,
  type DiscordMember,
} from '@swtorbit/shared-socket-events';
import { formatDate } from '../utils/formatDate';
import { useDiscordContext } from '../contexts/DiscordContext';
import { useRegistrations } from '../hooks/useRegistrations';
import { ConfirmModal } from '../ui/ConfirmModal';

interface RegistrationsListProps {
  /** Database id of the game; the list and export are requested by it. */
  gameId: number;
  roomCode: string;
  gameType: GameType;
  /** An archived game is outside the live engine: no live pushes are for it. */
  archived?: boolean;
  /** The game's Discord server: its members can be signed up by hand. */
  guildId?: string;
}

interface RowProps {
  r: Registration;
  /** The member's server display name, when known. */
  displayName?: string;
  onRemove?: (r: Registration) => void;
}

function DiscordCell({ r, displayName }: RowProps) {
  return (
    <td>
      <div>{displayName ?? r.discordUsername}</div>
      <div className="text-slate fs-xs">
        {displayName && displayName !== r.discordUsername ? `@${r.discordUsername} · ` : ''}{r.discordId}
      </div>
    </td>
  );
}

function RemoveCell({ r, onRemove }: RowProps) {
  if (!onRemove) return null;
  return (
    <td>
      <button type="button" className="btn btn--outline btn--red btn--sm" onClick={() => onRemove(r)}>
        Remove
      </button>
    </td>
  );
}

function FprRow(props: RowProps) {
  const { r } = props;
  const data = r.responses as FprRegistrationResponses;
  const roles = data?.roles?.length ? data.roles.map((x) => x.toUpperCase()).join(' / ') : '—';
  return (
    <tr>
      <DiscordCell {...props} />
      <td>{roles}</td>
      <td>{data?.availableAsLeader ? '✓' : ''}</td>
      <td>{data?.knowsMmMechanics ? '✓' : ''}</td>
      <td>{formatDate(r.createdAt)}</td>
      <RemoveCell {...props} />
    </tr>
  );
}

function PlainRow(props: RowProps) {
  return (
    <tr>
      <DiscordCell {...props} />
      <td>{formatDate(props.r.createdAt)}</td>
      <RemoveCell {...props} />
    </tr>
  );
}

function memberLabel(m: DiscordMember): string {
  return m.displayName && m.displayName !== m.username ? `${m.displayName} (@${m.username})` : `@${m.username}`;
}

/**
 * Sign a member of the game's Discord server up by hand: type to search the
 * server's members, pick one, Add.
 */
function AddSignupForm({ listId, members, onAdd }: {
  listId: string;
  members: DiscordMember[];
  onAdd: (member: DiscordMember) => void;
}) {
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const byLabel = useMemo(() => new Map(members.map((m) => [memberLabel(m), m])), [members]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const member = byLabel.get(query.trim());
    if (!member) {
      setError('Pick someone from the list of server members.');
      return;
    }
    setError(null);
    setQuery('');
    onAdd(member);
  };

  return (
    <form className="d-flex align-items-center gap-xs mb-sm" onSubmit={submit}>
      <input
        className="input"
        list={listId}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Add a sign-up: search your Discord server's members"
        aria-label="Discord member to sign up"
      />
      <datalist id={listId}>
        {members.map((m) => <option key={m.id} value={memberLabel(m)} />)}
      </datalist>
      <button type="submit" className="btn btn--sm" disabled={query.trim() === ''}>Add</button>
      {error && <span className="text-red fs-sm">{error}</span>}
    </form>
  );
}

function recordPreviewLabel(record: RegistrationExportRecord, gameType: GameType): string {
  const name = record.discordUsername ?? record.playerId?.slice(-8) ?? 'Unknown';
  if (gameType === GameType.FP_RACE) {
    const data = record.responses as FprRegistrationResponses;
    const roles = data?.roles?.map((r) => r.toUpperCase()).join('/') ?? '—';
    return `${name} — ${roles}`;
  }
  return name;
}

interface ImportState {
  file: RegistrationsExportFile;
  override: boolean;
}

interface ImportPreview {
  toAdd: number;
  toUpdate: number;
  toSkip: number;
}

export function RegistrationsList({ gameId, roomCode, gameType, archived = false, guildId }: RegistrationsListProps) {
  const { socket } = useSocketContext();
  const { members, fetchChannelsAndMembers } = useDiscordContext();
  const registrations = useRegistrations({ gameId, roomCode, archived });
  const [removing, setRemoving] = useState<Registration | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);
  // FP Race asks questions on Discord; the other games' sign-ups are one click,
  // so the admin can add them too. An archived game is read-only.
  const canAdd = !archived && gameType !== GameType.FP_RACE;
  const canRemove = !archived;

  useEffect(() => {
    if (canAdd && guildId) fetchChannelsAndMembers(guildId);
  }, [canAdd, guildId, fetchChannelsAndMembers]);

  const displayNames = useMemo(() => new Map(members.map((m) => [m.id, m.displayName || m.username])), [members]);
  const addableMembers = useMemo(() => {
    const signedUp = new Set(registrations.map((r) => r.discordId));
    return members
      .filter((m) => !m.bot && !signedUp.has(m.id))
      .sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)));
  }, [members, registrations]);

  const ackChange = (ack: RegistrationChangeAck) => setChangeError(ack?.success ? null : (ack?.error ?? 'The change failed'));

  const handleAdd = useCallback((member: DiscordMember) => {
    if (!socket) return;
    socket.emit(
      SocketEvent.REGISTRATION_ADD,
      { roomCode, discordId: member.id, discordUsername: member.username } satisfies RegistrationAddRequest,
      ackChange,
    );
  }, [socket, roomCode]);

  const handleConfirmRemove = useCallback(() => {
    if (!socket || !removing) return;
    socket.emit(
      SocketEvent.REGISTRATION_REMOVE,
      { roomCode, registrationId: removing.id } satisfies RegistrationRemoveRequest,
      ackChange,
    );
    setRemoving(null);
  }, [socket, roomCode, removing]);
  const [importState, setImportState] = useState<ImportState | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ added: number; updated: number; skipped: number } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!socket) return;
    const handleExportData = (data: ExportRegistrationsDataResponse) => {
      if (data.gameId !== gameId) return;
      setIsExporting(false);
      if (!data.success || !data.exportFile) return;

      const json = JSON.stringify(data.exportFile, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const gameName = data.exportFile.sourceGame.name.replace(/\s+/g, '_');
      a.href = url;
      a.download = `registrations_${gameName}_${data.exportFile.sourceGame.roomCode}.json`;
      a.click();
      URL.revokeObjectURL(url);
    };
    const handleImported = (data: RegistrationsImportedResponse) => {
      if (archived || data.roomCode !== roomCode) return;
      setIsImporting(false);
      if (!data.success) {
        setImportError(data.error ?? 'Import failed');
        return;
      }
      setImportResult({ added: data.added, updated: data.updated, skipped: data.skipped });
      setImportState(null);
    };

    socket.on(SocketEvent.EXPORT_REGISTRATIONS_DATA, handleExportData);
    socket.on(SocketEvent.REGISTRATIONS_IMPORTED, handleImported);
    return () => {
      socket.off(SocketEvent.EXPORT_REGISTRATIONS_DATA, handleExportData);
      socket.off(SocketEvent.REGISTRATIONS_IMPORTED, handleImported);
    };
  }, [socket, gameId, roomCode, archived]);

  const handleExport = useCallback(() => {
    if (!socket || isExporting) return;
    setIsExporting(true);
    socket.emit(SocketEvent.EXPORT_REGISTRATIONS_REQUEST, { gameId } satisfies ExportRegistrationsRequest);
  }, [socket, gameId, isExporting]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so same file can be re-selected
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string) as RegistrationsExportFile;
        if (!parsed.registrations || !parsed.sourceGame?.gameType) {
          setImportError('Invalid export file format.');
          return;
        }
        setImportError(null);
        setImportResult(null);
        setImportState({ file: parsed, override: false });
      } catch {
        setImportError('Failed to parse file — must be a valid JSON export.');
      }
    };
    reader.readAsText(file);
  }, []);

  const importPreview = useMemo((): ImportPreview | null => {
    if (!importState) return null;
    const existingDiscordIds = new Set(registrations.map((r) => r.discordId));
    let toAdd = 0;
    let toUpdate = 0;
    let toSkip = 0;
    for (const record of importState.file.registrations) {
      const discordId = record.discordId;
      if (discordId && existingDiscordIds.has(discordId)) {
        if (importState.override) toUpdate++;
        else toSkip++;
      } else {
        toAdd++;
      }
    }
    return { toAdd, toUpdate, toSkip };
  }, [importState, registrations]);

  const handleConfirmImport = useCallback(() => {
    if (!socket || !importState || isImporting) return;
    setIsImporting(true);
    socket.emit(SocketEvent.IMPORT_REGISTRATIONS, {
      roomCode,
      exportFile: importState.file,
      override: importState.override,
    });
  }, [socket, roomCode, importState, isImporting]);

  const rows = useMemo(() => {
    if (registrations.length === 0) {
      return (
        <tr>
          <td colSpan={(gameType === GameType.FP_RACE ? 5 : 2) + (canRemove ? 1 : 0)} className="text-slate text-center">
            No registrations yet
          </td>
        </tr>
      );
    }
    const onRemove = canRemove ? setRemoving : undefined;
    const Row = gameType === GameType.FP_RACE ? FprRow : PlainRow;
    return registrations.map((r) => (
      <Row key={r.id} r={r} displayName={displayNames.get(r.discordId)} onRemove={onRemove} />
    ));
  }, [registrations, gameType, canRemove, displayNames]);

  const gameTypeMismatch = importState
    ? importState.file.sourceGame.gameType !== gameType
    : false;

  const previewRecords = importState?.file.registrations.slice(0, 3) ?? [];

  return (
    <div className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base glow glow-blue-light p-lg mb-lg">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>Registrations ({registrations.length})</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn btn--outline btn--slate btn--sm"
            onClick={handleExport}
            disabled={isExporting || registrations.length === 0}
          >
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            className="btn btn--outline btn--slate btn--sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      {importResult && (
        <div className="text-green-dark" style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
          Import complete — {importResult.added} added, {importResult.updated} updated, {importResult.skipped} skipped.
        </div>
      )}

      {canAdd && (guildId ? (
        <AddSignupForm listId={`signup-members-${gameId}`} members={addableMembers} onAdd={handleAdd} />
      ) : (
        <p className="text-slate fs-sm mb-sm">Choose this game's Discord server in Discord Settings to add sign-ups by hand.</p>
      ))}
      {changeError && <p className="text-red fs-sm mb-sm">{changeError}</p>}

      <table className="w-full">
        <thead>
          {gameType === GameType.FP_RACE ? (
            <tr>
              <th>Discord</th>
              <th>Roles</th>
              <th>Leader</th>
              <th>MM</th>
              <th>Registered</th>
              {canRemove && <th />}
            </tr>
          ) : (
            <tr>
              <th>Discord</th>
              <th>Registered</th>
              {canRemove && <th />}
            </tr>
          )}
        </thead>
        <tbody>{rows}</tbody>
      </table>

      <ConfirmModal
        isOpen={removing !== null}
        onConfirm={handleConfirmRemove}
        onCancel={() => setRemoving(null)}
        title="Remove sign-up"
        message={removing ? `Remove ${displayNames.get(removing.discordId) ?? removing.discordUsername} from the sign-ups? The Discord post updates too.` : ''}
        confirmText="Remove"
        variant="danger"
      />

      {/* Import preview modal */}
      <Modal open={importState !== null} onClose={() => { setImportState(null); setImportError(null); }} title="Import Registrations" footer={
<>
          <button className="btn btn--outline btn--slate" onClick={() => { setImportState(null); setImportError(null); }}>
            Cancel
          </button>
          {!gameTypeMismatch && (
            <button
              className="btn"
              onClick={handleConfirmImport}
              disabled={isImporting}
            >
              {isImporting ? 'Importing…' : 'Confirm Import'}
            </button>
          )}
        </>
}>
          {importState && (
            <div className="d-flex flex-column gap-sm text-white">
              {gameTypeMismatch ? (
                <div className="text-red">
                  Game type mismatch: this export is for a <strong>{importState.file.sourceGame.gameType.toUpperCase()}</strong> game
                  but the current game is <strong>{gameType.toUpperCase()}</strong>. Import is not allowed.
                </div>
              ) : (
                <>
                  <div>
                    <div className="fs-xs">Source game</div>
                    <div><strong>{importState.file.sourceGame.name}</strong> ({importState.file.sourceGame.roomCode})</div>
                    <div className="fs-xs">
                      Exported {new Date(importState.file.exportedAt).toLocaleString()}
                    </div>
                  </div>

                  <div>
                    <div>
                      <strong>{importState.file.registrations.length}</strong> registration{importState.file.registrations.length !== 1 ? 's' : ''} to import into <strong>{roomCode}</strong>
                    </div>
                    {importPreview && (
                      <div className="fs-sm mt-2xs">
                        {importPreview.toAdd > 0 && <span>{importPreview.toAdd} new</span>}
                        {importPreview.toUpdate > 0 && <span>{importPreview.toAdd > 0 ? ', ' : ''}{importPreview.toUpdate} will update</span>}
                        {importPreview.toSkip > 0 && <span>{(importPreview.toAdd > 0 || importPreview.toUpdate > 0) ? ', ' : ''}{importPreview.toSkip} will be skipped (already exist)</span>}
                      </div>
                    )}
                  </div>

                  <label className="d-flex align-items-center gap-xs" style={{ cursor: 'pointer' }}>
                    <input
                      className="checkbox"
                      type="checkbox"
                      checked={importState.override}
                      onChange={(e) => setImportState((s) => s ? { ...s, override: e.target.checked } : s)}
                    />
                    Override existing registrations (update players already in this game)
                  </label>

                  {previewRecords.length > 0 && (
                    <div>
                      <div className="fs-xs mb-2xs">Preview</div>
                      {previewRecords.map((record, i) => (
                        <div key={i} className="fs-sm">
                          {recordPreviewLabel(record, gameType)}
                        </div>
                      ))}
                      {importState.file.registrations.length > 3 && (
                        <div className="fs-xs">
                          …and {importState.file.registrations.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {importError && (
                <div className="text-red fs-sm">{importError}</div>
              )}
            </div>
          )}
        </Modal>
    </div>
  );
}
